import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { getVoiceInfo } from '@/components/Voice/voiceInfo';
import { useAllVoices } from '@/hooks/useAllVoices';
import ArrowDown from '@/images/ArrowDown.svg';
import CloneVoice from '@/images/CloneVoice.svg';
import FaceFemale from '@/images/FaceFemale.svg';
import FaceMale from '@/images/FaceMale.svg';
import FlashLightning2 from '@/images/FlashLightning2.svg';
import { useGetExternalKey } from '@/pages/AdvancedEditor/hooks';
import { ttsVoiceModal } from '@/state/presenters';
import theme from '@/theme';
import { getLanguageFlagSrc } from '@/utils/getLangaugeFlagSrc';
import { isProPlus } from '@/utils/isProPlus';
import { ExternalKeysProviders, IVoice } from '@d-id/types';
import styled from '@emotion/styled';
import { ButtonBase } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { observer } from 'mobx-react-lite';
import { useSetRecoilState } from 'recoil';
import { AdvancedEditorVoiceModal } from './VoiceModal';

const StyledButton = styled(ButtonBase)`
    padding: 2.5rem;
    border: ${theme.colors.black[200]} 1px solid;
    border-radius: 8px;
    background-color: ${theme.colors.primary.bgLight};
    box-shadow: 0px 1px 2px 0px rgba(187, 187, 187, 0.12);

    :hover {
        background-color: ${theme.colors.primary.bgMedium};
    }
`;

const DataContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    width: 100%;
`;

const VoiceContainer = styled.div`
    display: flex;
    color: ${theme.colors.secondary[900]};
    font-size: 16px;
    font-weight: 500;
    align-items: center;

    img {
        margin-right: 8px;
    }
`;

const LanguageContainer = styled.div`
    display: flex;
    align-items: center;
    white-space: nowrap;
    color: ${theme.colors.secondary[700]};
    font-size: 14px;
    line-height: 1.2;

    img {
        margin-right: 10px;
    }
`;

const StyledCircularProgress = styled(CircularProgress)`
    margin: 16px auto;
`;

const RightArrow = styled(MaskedIcon)`
    transform: rotate(-90deg);
    color: ${theme.colors.black[500]};
`;

const LanguageImage = styled.img`
    width: 16px;
    margin: 0 2px;
`;

const InfoContainer = styled.div`
    display: block;
    width: 245px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-align: start;

    @media (max-width: 1080px) {
        width: 200px;
    }
`;

const FlashLightningIcon = styled(MaskedIcon)`
    color: ${theme.colors.primary[900]};
    margin: 0;
`;

const genderImage: Record<string, string> = {
    female: FaceFemale,
    male: FaceMale,
    'non-binary': CloneVoice,
};

const getGenderIcon = (voice: IVoice): JSX.Element | null => {
    const gender = voice.gender;
    return gender && gender !== 'unspecified' ? (
        <img loading="lazy" width="20" height="20" src={genderImage[gender.toLowerCase()]} alt="start adornment" />
    ) : null;
};

const getLanguageIcon = (value: IVoice): JSX.Element | null => {
    const src = getLanguageFlagSrc(value);
    return src ? <LanguageImage loading="lazy" src={src} alt={value.language} /> : null;
};

interface Props {
    ttsSelectedVoice: IVoice | null;
}

export const VoiceButton = observer(({ ttsSelectedVoice }: Props) => {
    // this allows us to preload voices before modal open
    useAllVoices();
    useGetExternalKey(ExternalKeysProviders.Elevenlabs);

    const setVoiceModalState = useSetRecoilState(ttsVoiceModal);

    const handleModalOpen = () => {
        setVoiceModalState(true);
    };

    const isProVoice = isProPlus(ttsSelectedVoice);

    if (!ttsSelectedVoice) {
        return <StyledCircularProgress />;
    }

    return (
        <>
            <StyledButton onClick={handleModalOpen} id="voice-select-btn" data-testid="voice-select-button">
                <DataContainer>
                    <VoiceContainer>
                        {getGenderIcon(ttsSelectedVoice)}
                        {ttsSelectedVoice.name}
                        {isProVoice && <FlashLightningIcon src={FlashLightning2} size="16px" />}
                    </VoiceContainer>
                    <LanguageContainer>
                        {getLanguageIcon(ttsSelectedVoice)}
                        <InfoContainer>{getVoiceInfo(ttsSelectedVoice)}</InfoContainer>
                    </LanguageContainer>
                </DataContainer>
                <RightArrow src={ArrowDown} size="32px" />
            </StyledButton>
            <AdvancedEditorVoiceModal />
        </>
    );
});
